import React, { useState } from "react";
import { Typography, CircularProgress, Backdrop } from "@mui/material";
import {
  MainContainer,
  LoginPanel,
  Content,
  HeroImage,
  FormContainer,
  Logo,
  Form,
  Footer,
  ForgotPasswordLink,
  ForgotPasswordText,
  LogoContainer,
  Title,
} from "./Login.styles";
import api from "../../api/config";
import { ForgotPassword as ForgotPasswordApi } from "../../api/Authentication";
import Notification from "../../components/shared/Notification";
import mammo_login_image from "../../assets/images/login_viewer_list1.png";
import nexus_logo from "../../assets/images/MOVU_logo_white.png";
import { StyledTextFieldGroup } from "../../components/shared/StyledTextFieldGroup";
import { ActionButton } from "../../components/shared/action-button";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import resetPasswordMail from "../../assets/svg/reset-password-mail.svg";

const ForgotPassword = ({ showNotification }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [isLinkSent, setIsLinkSent] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post(
        `${ForgotPasswordApi}?login_location=dashboard`,
        { email }
      );
      console.log("Reset password response:", response);

      const message = response.data.status || "Unexpected response";
      setNotification({
        open: true,
        message,
        severity: "success",
      });
      setIsLinkSent(true);
    } catch (error) {
      console.error("Reset password error:", error);
      const errorMessage =
        error.response?.data?.detail || "Failed to send reset link";
      setNotification({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainContainer>
      <Notification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        handleClose={() => setNotification({ ...notification, open: false })}
      />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <LoginPanel>
        <Content>
          <HeroImage src={mammo_login_image} alt="Hero" />
          <FormContainer>
            <div style={{ display: "flex", flexDirection: "column", gap: "36px"}}>
              <LogoContainer>
                <Logo src={nexus_logo} alt="Logo" />
              </LogoContainer>
              {!isLinkSent ? (
                <Form onSubmit={handleResetPassword}>
                  <div>
                    <Title variant="b">
                      Forgot Password
                    </Title>
                    <Typography
                      variant="body2"
                      style={{ marginBottom: "20px", color: "#F8F8F8" }}
                    >
                      Enter your email address, and we will send you a link to reset
                      your password.
                    </Typography>
                  </div>
                  <StyledTextFieldGroup
                    label="Email"
                    placeholder="example@email.com"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    margin="none"
                    InputLabelProps={{
                      shrink: Boolean(email) || undefined,
                    }}
                  />
                  <div style={{ marginTop: "24px" }}>
                    <ActionButton type="submit" xs={{ height: "50px" }} fullWidth>
                      Send Reset Link
                    </ActionButton>
                  </div>
                  <ForgotPasswordText
                    variant="body2"
                    style={{ marginTop: "32px" }}
                  >
                    <ForgotPasswordLink href="/login">
                      <div style={{ display: "flex", justifyContent: "left" }}>
                        <ChevronLeftIcon></ChevronLeftIcon>
                        <div style={{ marginTop: "auto", marginBottom: "auto" }}>
                          Back to Login
                        </div>
                      </div>
                    </ForgotPasswordLink>
                  </ForgotPasswordText>
                </Form>
              ) : (
                <div style={{ textAlign: "center" }}>
                  <div>
                    <img src={resetPasswordMail} alt="Reset Password Mail" />
                  </div>
                  <Title variant="b">
                      Password Reset Link Sent
                  </Title>
                  <Typography
                    variant="body2"
                    style={{ marginBottom: "10px", color: "#F8F8F8" }}
                  >
                    A password reset link has been sent to {email}.
                    <br />
                    Please check your email.
                  </Typography>
                  <hr style={{ 
                    borderColor: "#9DA2A7", 
                    marginBottom: "36px", 
                    marginTop: "36px",
                  }} />
                  <Typography
                    variant="body2"
                    style={{ color: "#F8F8F8", marginBottom: "24px" }}
                  >
                    Didn’t receive the email? Click here!{" "}
                    <ForgotPasswordLink href="#" onClick={handleResetPassword}>
                      Resend the link.
                    </ForgotPasswordLink>
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      color: "#F8F8F8",
                      marginBottom: "24px",
                    }}
                  >
                    For further assistance, call us at <b>415-523-4442</b>.
                  </Typography>
                  <ForgotPasswordLink href="/login">
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <div style={{ marginTop: "auto", marginBottom: "auto" }}>
                        <ChevronLeftIcon></ChevronLeftIcon>
                      </div>
                      <div style={{ marginTop: "auto", marginBottom: "auto" }}>
                        Back to Login
                      </div>
                    </div>
                  </ForgotPasswordLink>
                </div>
              )}
            </div>
            <Footer>
              <Typography
                variant="body2"
                style={{ color: "#9DA2A7", marginTop: "30px" }}
              >
                Powered by Nexus-MD
              </Typography>
            </Footer>
          </FormContainer>
        </Content>
      </LoginPanel>
    </MainContainer>
  );
};

export default ForgotPassword;
